import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MRT_ColumnDef } from 'material-react-table'
import Add from '@mui/icons-material/Add'
import PersonRemove from '@mui/icons-material/PersonRemove'
import { Typography, Button, Alert, Tooltip, IconButton } from '@mui/material'
import { useStytchMember } from '@stytch/react/b2b'
import { Member } from '@stytch/vanilla-js'
import { useQueryClient } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import {
  useGetAuthRoles,
  useGetAuthUserList,
} from '@/service-library/hooks/auth-user'
import { AuthUser } from '@/types/users'
import { getHighestRole, getRemoveUserPermission } from '@/utils/auth-user'
import { showErrorSnackbar } from '@/utils/snackbars'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import BaseUsersTable from '@/components/user-table/BaseUsersTable'
import AddUserDialog from './AddUserDialog'
import RemoveUserDialog from './RemoveUserDialog'

export default function UsersTable() {
  const navigate = useNavigate()
  const { currentAccount } = useCurrentAccountContext()
  const { member } = useStytchMember()
  const isInSameAccount =
    member?.organization_id === currentAccount.stytch_org_id

  const { users, isPending, isError, queryKey } = useGetAuthUserList({
    enabled: isInSameAccount,
  })

  const queryClient = useQueryClient()

  const { roles, isPending: rolesIsPending } = useGetAuthRoles({
    enabled: isInSameAccount,
  })
  const rolesMap = useMemo(
    () => new Map(roles.map((role) => [role.id, role])),
    [roles],
  )

  const addOverlay = useOverlay()
  const removeOverlay = useOverlay()
  const { open: openRemoveOverlay } = removeOverlay
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null)

  const columns: MRT_ColumnDef<AuthUser>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableClickToCopy: true,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableClickToCopy: true,
      },
      {
        accessorFn: (row) => row.employee_ext_id || '',
        header: 'Employee ID',
        enableClickToCopy: true,
      },
      {
        id: 'roles',
        header: 'Role',
        accessorFn: (row) => getHighestRole(row.roles ?? [], rolesMap).id,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {cell.getValue() as string}
          </Typography>
        ),
      },
      {
        id: 'remove',
        header: '',
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        size: 50,
        Cell: ({ row }) => {
          const targetMember = row.original.stytch_member
          if (!targetMember) return null
          const { canRemoveUser, tooltip } = getRemoveUserPermission({
            loggedInMember: member as Member,
            targetMember,
            rolesMap,
          })
          return (
            <Tooltip enterDelay={500} title={tooltip}>
              <div>
                <IconButton
                  color="primary"
                  disabled={!canRemoveUser}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    setMemberToRemove(targetMember)
                    openRemoveOverlay()
                  }}
                >
                  <PersonRemove fontSize="small" />
                </IconButton>
              </div>
            </Tooltip>
          )
        },
      },
    ],
    [member, openRemoveOverlay, rolesMap],
  )

  useEffect(() => {
    if (isError) {
      showErrorSnackbar('Failed to fetch users.')
    }
  }, [isError])

  if (!isInSameAccount) {
    return (
      <Alert severity="info">
        You are not logged into the current account. You can only view users
        that belong to the account you are logged into.
      </Alert>
    )
  }

  return (
    <>
      <BaseUsersTable
        users={users}
        columns={columns}
        enableMultiRowSelection={false}
        isLoading={isPending || rolesIsPending}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => {
            const userKey =
              row.original.id ?? row.original.stytch_member?.member_id
            if (userKey) navigate(`/settings/users/${userKey}`)
          },
          sx: {
            cursor: 'pointer',
          },
        })}
        renderTableActions={() => (
          <Button
            onClick={() => {
              addOverlay.open()
            }}
            startIcon={<Add />}
            variant="text"
          >
            New User
          </Button>
        )}
      />
      <AddUserDialog overlay={addOverlay} />
      <RemoveUserDialog
        overlay={removeOverlay}
        member={memberToRemove}
        onRemoved={() => {
          setMemberToRemove(null)
          queryClient.invalidateQueries({ queryKey })
        }}
      />
    </>
  )
}
